import { scrypt } from '@noble/hashes/scrypt';
import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import {
  randomBytes, b64encode, b64decode, utf8, normalizeSecret, bytesToHex, getPublicKey, npubEncode,
} from '@herald/core';

/**
 * Passphrase-encrypted key backup.
 *
 * Key loss is the single biggest usability risk in the design (HP-07): there is
 * no password reset, and losing the key loses every subscription. Showing the
 * raw `nsec` and hoping the user writes it down is not a backup strategy --
 * people paste it into notes apps and cloud drives.
 *
 * So the backup is a small encrypted envelope instead: safe to keep in cloud
 * storage or e-mail to yourself, useless to anyone without the passphrase. The
 * envelope is self-describing so a future implementation can still open it.
 *
 * scrypt N=2^16 costs roughly 200 ms and 64 MB, which is a deliberate trade:
 * high enough to make a stolen envelope expensive to attack offline, low
 * enough to run on a phone.
 */

export const BACKUP_KDF = { N: 2 ** 16, r: 8, p: 1, dkLen: 32 };
export const MIN_PASSPHRASE_LENGTH = 10;

function deriveKey(passphrase, salt) {
  return scrypt(utf8(passphrase.normalize('NFKC')), salt, BACKUP_KDF);
}

/**
 * @param {Uint8Array|string} secretKey
 * @param {string} passphrase
 * @returns {object} the envelope, ready to serialise as JSON
 */
export function encryptBackup(secretKey, passphrase) {
  if (typeof passphrase !== 'string' || passphrase.length < MIN_PASSPHRASE_LENGTH) {
    throw new Error(`heslo musí mít alespoň ${MIN_PASSPHRASE_LENGTH} znaků`);
  }
  const sk = normalizeSecret(secretKey);
  const salt = randomBytes(16);
  const nonce = randomBytes(24);
  const sealed = xchacha20poly1305(deriveKey(passphrase, salt), nonce).encrypt(sk);

  return {
    herald_backup: 1,
    // The public key is stored in the clear on purpose: it lets a restore
    // screen show whose identity the file holds before asking for a passphrase,
    // and it reveals nothing that is not already public.
    npub: npubEncode(getPublicKey(sk)),
    kdf: { name: 'scrypt', ...BACKUP_KDF },
    cipher: 'xchacha20poly1305',
    salt: b64encode(salt),
    nonce: b64encode(nonce),
    ciphertext: b64encode(sealed),
    created_at: Math.floor(Date.now() / 1000),
  };
}

/**
 * @param {object|string} envelope parsed JSON or the raw file contents
 * @param {string} passphrase
 * @returns {string} the secret key as 32-byte hex
 */
export function decryptBackup(envelope, passphrase) {
  const doc = typeof envelope === 'string' ? JSON.parse(envelope) : envelope;
  if (!doc || doc.herald_backup !== 1) throw new Error('to není záloha Herald klíče');
  if (doc.cipher !== 'xchacha20poly1305' || doc.kdf?.name !== 'scrypt') {
    throw new Error('záloha používá neznámý formát šifrování');
  }
  const salt = b64decode(doc.salt);
  const nonce = b64decode(doc.nonce);
  const params = { N: doc.kdf.N, r: doc.kdf.r, p: doc.kdf.p, dkLen: doc.kdf.dkLen ?? 32 };
  // Refuse absurd parameters rather than letting a crafted file exhaust memory.
  if (!(params.N > 0 && params.N <= 2 ** 20) || params.r > 32 || params.p > 16) {
    throw new Error('záloha má nepřijatelné parametry odvození klíče');
  }

  const key = scrypt(utf8(passphrase.normalize('NFKC')), salt, params);
  let secretKey;
  try {
    secretKey = xchacha20poly1305(key, nonce).decrypt(b64decode(doc.ciphertext));
  } catch {
    // AEAD failure is indistinguishable from a wrong passphrase, which is the
    // only thing worth telling the user.
    throw new Error('nesprávné heslo, nebo je záloha poškozená');
  }
  if (secretKey.length !== 32) throw new Error('záloha neobsahuje platný klíč');

  const restored = npubEncode(getPublicKey(secretKey));
  if (doc.npub && doc.npub !== restored) {
    throw new Error('záloha je vnitřně nekonzistentní a byla odmítnuta');
  }
  return bytesToHex(secretKey);
}

export function backupFilename(npub) {
  const date = new Date().toISOString().slice(0, 10);
  return `herald-zaloha-${npub.slice(5, 13)}-${date}.json`;
}
